import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API } from "./api";
import { getJSON, setJSON, remove, K } from "./storage";

const AuthCtx = createContext(null);

function formatDetail(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authFetch = useCallback(async (path, opts = {}) => {
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    const t = getJSON(K.token, null);
    if (t) headers["Authorization"] = `Bearer ${t}`;
    return fetch(`${API}${path}`, { ...opts, headers });
  }, []);

  useEffect(() => {
    (async () => {
      const t = getJSON(K.token, null);
      if (!t) {
        setLoading(false);
        return;
      }
      try {
        const r = await authFetch("/auth/me");
        if (r.ok) {
          const d = await r.json();
          setUser(d.user);
        } else {
          remove(K.token);
        }
      } catch {}
      setLoading(false);
    })();
  }, [authFetch]);

  const login = async (email, password) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(formatDetail(d.detail));
      const ra = r.headers.get("Retry-After");
      if (ra) err.retryAfter = parseInt(ra, 10);
      throw err;
    }
    setJSON(K.token, d.token);
    setUser(d.user);
    return d.user;
  };

  const register = async (email, password, name, term) => {
    const r = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name, term }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(formatDetail(d.detail));
    setJSON(K.token, d.token);
    setUser(d.user);
    return d.user;
  };

  const logout = useCallback(() => {
    remove(K.token);
    setUser(null);
  }, []);

  // Déconnexion automatique après 15 min d'inactivité (sécurité dashboard).
  useEffect(() => {
    if (!user) return;
    const LIMIT = 15 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(), LIMIT);
    };
    const evts = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      evts.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const changePassword = async (current_password, new_password) => {
    const r = await authFetch("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatDetail(d.detail));
    if (d.token) setJSON(K.token, d.token);
    return true;
  };

  const syncProfile = async (name, term) => {
    try {
      const r = await authFetch("/auth/profile", { method: "PUT", body: JSON.stringify({ name, term }) });
      if (r.ok) {
        const d = await r.json();
        setUser(d.user);
      }
    } catch {}
  };

  const deleteServerAccount = async () => {
    try {
      await authFetch("/auth/account", { method: "DELETE" });
    } catch {}
    logout();
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, syncProfile, deleteServerAccount, changePassword, authFetch }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
